// ─── Profiel-generator — medewerker-profiel (.pptx) + AI-concept ─────────────
//
// Datamodel: zie packages/db/prisma/schema.prisma — enum ProfielFunctie +
// EmployeeProfiel (profielTekst / profielFuncties / kerncompetenties) +
// EmployeeWerkervaring.
//
// STABIEL CONTRACT: deze types worden gedeeld tussen de API (profiel-routes +
// pptx-fill-engine + AI-concept-service) en de backoffice Profiel-tab. Pas de
// namen/shapes niet aan zonder de afnemers mee te nemen.

// ─── Functie-checkboxes (matching Prisma enum ProfielFunctie) ────────────────

export const PROFIEL_FUNCTIES = [
  'HTD',
  'MANAGEMENT',
  'STORINGSMONTEUR',
  'PROJECTMANAGER',
  'SOFTWARE',
  'TEKENAAR',
] as const
export type ProfielFunctie = (typeof PROFIEL_FUNCTIES)[number]

export const PROFIEL_FUNCTIE_LABELS: Record<ProfielFunctie, string> = {
  HTD: 'HTD',
  MANAGEMENT: 'Management',
  STORINGSMONTEUR: 'Storingsmonteur',
  PROJECTMANAGER: 'Projectmanager',
  SOFTWARE: 'Software',
  TEKENAAR: 'Tekenaar',
}

// ─── Werkervaring (EmployeeWerkervaring CRUD) ────────────────────────────────

export interface EmployeeWerkervaringDTO {
  id: string
  functie: string
  bedrijf: string | null
  periodeVan: string | null
  periodeTot: string | null
  bullets: string[]
  volgorde: number
}

export interface EmployeeWerkervaringInput {
  functie: string
  bedrijf?: string | null
  periodeVan?: string | null
  periodeTot?: string | null
  bullets?: string[]
  volgorde?: number
}

// ─── Profiel-data (input voor de pptx fill-engine) ───────────────────────────

export interface ProfielCheckbox {
  key: string
  label: string
  checked: boolean
}

export interface ProfielWerkervaringItem {
  functie: string
  bedrijf: string | null
  periode: string | null
  bullets: string[]
}

export interface ProfielBedrijfsgegevens {
  naam: string
  adres: string | null
  postcode: string | null
  plaats: string | null
  email: string | null
  kvk: string | null
  btw: string | null
  iban: string | null
}

// Alles wat de pptx-filler nodig heeft (JSON-serialiseerbaar; foto-bytes worden
// los doorgegeven door de route).
export interface ProfielData {
  naam: string
  geboortejaar: string | null
  woonplaats: string | null
  heeftFoto: boolean
  verplichteOpleidingen: ProfielCheckbox[]
  aanvullendeOpleidingen: ProfielCheckbox[]
  functies: ProfielCheckbox[]
  beschikbaarheid: ProfielCheckbox[]
  werkervaring: ProfielWerkervaringItem[]
  profielTekst: string | null
  // FO-304-E — klant-introductietekst (klantwaardig, los van `profielTekst`).
  klantProfielTekst: string | null
  kerncompetenties: string[]
  bedrijf: ProfielBedrijfsgegevens
}

// Read-only brondata die HR in de Profiel-tab ziet vóór het genereren.
export type ProfielPreviewData = ProfielData

// AI-concept (POST .../profiel/ai-concept) — een SUGGESTIE, nooit auto-opgeslagen.
export interface ProfielAiConceptResponse {
  profielTekst: string
  kerncompetenties: string[]
}

// ─── batch-42 profielschets (FO-368) — token-registry (bron van waarheid) ────
//
// ÉÉN lijst met token-definities, gedeeld door de fill-engine (profiel-pptx.ts),
// de upload-scan (profiel-template-scan.ts) en de token-legenda in de beheer-UI.
// Zo kan er geen drift ontstaan tussen "wat de scan herkent" en "wat de filler
// vult" (spec §11.2: geen tweede token-lijst).
//
// `key` is de CANONIEKE token-naam zonder delimiters; in de template staat hij
// als `{{key}}`. `aliassen` zijn extra herkende namen (achterwaartse compat met
// de repo-stand-in, die de kortere namen gebruikt). `profiel-foto` is GÉÉN
// `{{}}`-token maar een pic-shape-naamconventie (zie PROFIEL_FOTO_PLACEHOLDER_NAAM).

export type ProfielTokenKind = 'inline' | 'lijst' | 'checkbox' | 'foto'

export interface ProfielTokenDef {
  /** Canonieke token-naam zonder delimiters, bv. 'persoonlijk_profiel'. */
  key: string
  /** Extra herkende namen (bv. de kortere repo-stand-in-namen). */
  aliassen?: string[]
  kind: ProfielTokenKind
  /** Korte label voor de legenda. */
  label: string
  /** Uitleg + bron voor de legenda (zichtbare copy, STREEP-stijl). */
  beschrijving: string
  /** Klantwaardig veld dat naar buiten mag (nooit loon/interne data). */
  klantwaardig?: boolean
  /** Intern/legacy veld — afgeraden voor een klant-/sales-document. */
  intern?: boolean
}

/** Shape-naam/alt waarop de fill-engine álle profielfoto's herkent en vervangt. */
export const PROFIEL_FOTO_PLACEHOLDER_NAAM = 'profiel-foto'

export const PROFIEL_TOKENS: ProfielTokenDef[] = [
  { key: 'naam', kind: 'inline', label: 'Naam', beschrijving: 'Volledige naam van de medewerker.' },
  {
    key: 'geboortejaar',
    kind: 'inline',
    label: 'Geboortejaar',
    beschrijving: 'Jaar van geboorte (leeg als onbekend).',
  },
  {
    key: 'woonplaats',
    kind: 'inline',
    label: 'Woonplaats',
    beschrijving: 'Woonplaats van de medewerker.',
  },
  {
    key: 'persoonlijk_profiel',
    kind: 'lijst',
    label: 'Persoonlijk profiel',
    klantwaardig: true,
    beschrijving:
      'Klantwaardige introductietekst (HR-beheerd klantprofiel). Nooit interne notities of loon. Leeg = blok blijft leeg zonder token-restant.',
  },
  {
    key: 'werkervaring',
    kind: 'lijst',
    label: 'Werkervaring',
    beschrijving: 'Werkervaring-items: kop per functie + bullets.',
  },
  {
    key: 'kerncompetenties',
    kind: 'lijst',
    label: 'Kerncompetenties',
    beschrijving: 'Kerncompetenties, één bullet per regel.',
  },
  {
    key: 'opleidingen_verplicht_checked',
    aliassen: ['opleidingen_verplicht'],
    kind: 'checkbox',
    label: 'Verplichte opleidingen',
    beschrijving:
      'Verplichte opleidingen met een vakje: ☒ bij geldig bewijs, ☐ bij verlopen of ontbrekend.',
  },
  {
    key: 'opleidingen_aanvullend_checked',
    aliassen: ['opleidingen_aanvullend'],
    kind: 'checkbox',
    label: 'Aanvullende opleidingen',
    beschrijving: 'Aanvullende opleidingen met ☒/☐, zelfde certificaat-statuslogica.',
  },
  {
    key: 'functies_checked',
    aliassen: ['functies'],
    kind: 'checkbox',
    label: 'Functie',
    beschrijving:
      'Functie-vinkjes uit de beheerbare functie/skill-mapping (Instellingen). Niet hardcoded per template.',
  },
  {
    key: 'beschikbaarheid',
    kind: 'checkbox',
    label: 'Beschikbaarheid',
    beschrijving: 'Beschikbaarheid (dagdienst / ploegendienst / weekend / inval) met ☒/☐.',
  },
  {
    key: 'profieltekst',
    kind: 'lijst',
    label: 'Profieltekst (intern)',
    intern: true,
    beschrijving:
      'INTERNE profieltekst/AI-concept. Afgeraden voor een klant-/sales-document; gebruik "persoonlijk_profiel". Blijft ondersteund voor de standaard-fallback-template.',
  },
  {
    key: 'bedrijf_naam',
    kind: 'inline',
    label: 'Bedrijfsnaam',
    beschrijving: 'Naam van de BV/entiteit van de medewerker.',
  },
  {
    key: 'bedrijf_adres',
    kind: 'inline',
    label: 'Bedrijfsadres',
    beschrijving: 'Straat + huisnummer van de entiteit.',
  },
  {
    key: 'bedrijf_postcode_plaats',
    kind: 'inline',
    label: 'Postcode + plaats',
    beschrijving: 'Postcode en plaats van de entiteit.',
  },
  {
    key: 'bedrijf_email',
    kind: 'inline',
    label: 'E-mail',
    beschrijving: 'E-mailadres van de entiteit.',
  },
  { key: 'bedrijf_kvk', kind: 'inline', label: 'KvK', beschrijving: 'KvK-nummer van de entiteit.' },
  { key: 'bedrijf_btw', kind: 'inline', label: 'Btw', beschrijving: 'Btw-nummer van de entiteit.' },
  {
    key: 'bedrijf_iban',
    kind: 'inline',
    label: 'IBAN',
    beschrijving: 'IBAN van de entiteit.',
  },
  {
    key: PROFIEL_FOTO_PLACEHOLDER_NAAM,
    kind: 'foto',
    label: 'Profielfoto',
    beschrijving:
      'Naamconventie (géén {{token}}): elke afbeelding-shape met de naam "profiel-foto" wordt vervangen door de medewerkerfoto. Meerdere toegestaan (cover + detail-slides).',
  },
]

/** Alle herkende `{{...}}`-token-strings (canoniek + aliassen; excl. foto). */
export function profielTokenStrings(): string[] {
  const out: string[] = []
  for (const def of PROFIEL_TOKENS) {
    if (def.kind === 'foto') continue
    out.push(`{{${def.key}}}`)
    for (const alias of def.aliassen ?? []) out.push(`{{${alias}}}`)
  }
  return out
}

// ─── batch-42 profielschets — template-beheer DTO's ──────────────────────────

/** Resultaat van de token-scan bij upload (gevonden/ontbrekend/onbekend). */
export interface ProfielTemplateScanResultaat {
  /** Canonieke keys die (via de key of een alias) in de template staan. */
  gevonden: string[]
  /** Canonieke keys die NIET in de template staan. */
  ontbrekend: string[]
  /** `{{...}}`-tokens in de template die geen bekend token zijn (typfouten). */
  onbekend: string[]
  /** Aantal `profiel-foto`-placeholders (afbeelding-shapes). */
  fotoPlaceholders: number
}

/** Template-versie zoals getoond in de beheer-UI. */
export interface ProfielTemplateDTO {
  id: string
  versie: number
  bestandsnaam: string
  fileSize: number
  actief: boolean
  notitie: string | null
  scanResultaat: ProfielTemplateScanResultaat | null
  uploadedByNaam: string | null
  createdAt: string
  updatedAt: string
}

/** Eén beheerbare functie-vinkje-mapping-regel. */
export interface ProfielFunctieVinkjeDTO {
  id: string
  label: string
  volgorde: number
  /** Optionele koppeling aan de handmatige `Employee.profielFuncties`-keuze. */
  profielFunctie: ProfielFunctie | null
  functieTitels: string[]
  skillIds: string[]
  actief: boolean
}

/** Input-vorm voor de PUT-replace van de functie-vinkje-mapping. */
export interface ProfielFunctieVinkjeInput {
  label: string
  volgorde: number
  profielFunctie: ProfielFunctie | null
  functieTitels: string[]
  skillIds: string[]
  actief: boolean
}
